import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import ActivityIcon from '@passkeys/ui/activity-icon';
import Text from '@passkeys/ui/text';
import { useCallback } from 'react';

import ActivityStatus from '@/ui/components/activity-status';
import type { ActivityItem } from '@/ui/flux/tx-log/types';
import { getIsFailed } from '@/ui/flux/tx-log/utils';
import useConvertedAmount from '@/ui/hooks/use-converted-amount';
import useEnabledAssets from '@/ui/hooks/use-enabled-assets';
import { formatFiat } from '@/ui/utils/formatting';
import { useNavigate } from '@/ui/utils/navigation';
import { hasInvalidFromAsset } from '@/ui/utils/order';
import { formatTime } from '@/utils/datetime';

type TxRowProps = {
  activityItem: ActivityItem;
};

function TxRow({ activityItem }: TxRowProps) {
  const { title, date, icon, order, assetName, displayAmount, id } =
    activityItem;

  const assets = useEnabledAssets();
  const toAssetName = order ? order.toAsset : assetName;
  const toAsset = assets[toAssetName];
  const fromAsset = order ? assets[order.fromAsset] : undefined;

  const fiatAmount = useConvertedAmount({ amount: displayAmount });

  const navigate = useNavigate();

  if (!toAsset || hasInvalidFromAsset(order, fromAsset)) {
    throw new Error(`Invalid asset ${toAssetName}`);
  }

  const amount = order ? order.toAmount : displayAmount;
  const assetNames = order ? [toAsset.name, fromAsset.name] : [assetName];
  const assetDisplayNames = order
    ? [toAsset.displayName, fromAsset.displayName]
    : [assetName];

  const isFailed = getIsFailed(activityItem);

  const handleClick = useCallback(() => {
    navigate(`/transactions/${id}`);
  }, [navigate, id]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative -mx-4 -mt-px flex gap-4 rounded-xl border-y border-transparent p-4 transition-colors before:absolute before:inset-x-4 before:-bottom-px before:border-t before:border-solid before:border-black/6 before:transition-opacity hover:bg-black/6 hover:before:opacity-0 theme-dark:before:border-white/5 theme-dark:hover:bg-white/6 [&:has(+button:hover)]:before:opacity-0"
      data-testid="transaction-list-item-button"
    >
      <ActivityIcon
        assetNames={assetNames}
        displayNames={assetDisplayNames}
        typeIcon={icon}
      />
      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-x-1">
          <Text size={14}>{title}</Text>
          {isFailed && <ActivityStatus value="failed" size="small" />}
        </div>

        <Text size={14} className="text-gray-500 theme-dark:text-neutral-100">
          {formatTime(date)}
        </Text>
      </div>
      <div className="flex flex-col">
        {amount && (
          <Text size={14} align="right">
            {formatAssetAmount(amount.toDefaultNumber(), {
              useGrouping: true,
              maxSignificant: 6,
            })}{' '}
            {toAsset.displayTicker}
          </Text>
        )}
        {fiatAmount ? (
          <Text
            size={14}
            align="right"
            className="text-gray-400 theme-dark:text-neutral-200"
          >
            {formatFiat(fiatAmount)}
          </Text>
        ) : null}
      </div>
    </button>
  );
}

export default TxRow;
